import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';
import {
  SHAPES,
  SHAPE_SCALE_LEVELS,
  P3D_FOV_LEVELS,
  P3D_FACE_COLORS,
  P3D_EDGE_COLORS,
  P3D_TEXT_COLORS,
  FACE_LABELS,
  SPHERE_DETAIL_LEVELS,
  TORUS_TUBE_LEVELS,
  ICOSA_DETAIL_LEVELS,
  PARAMETRIC_TYPES,
  DEFAULT_PRIMITIVES3D,
} from '../../constants/primitives3d';
import { GRADIENT_BASE_TONES } from '../../constants/backgrounds';
import { DarkenSlider, ColorSwatchGrid, LevelPicker, SliderRow, PresetGrid, ToggleSwitch, TextInput, CollapseTransition } from '../../sdk';

/* ── Per-shape parameter panels ──────────────────────────────────── */

function CubeParams({ c, update }) {
  const updateFace = (index, text) => {
    const newFaces = [...(c.faces || DEFAULT_PRIMITIVES3D.faces)];
    newFaces[index] = text;
    update({ faces: newFaces });
  };
  return (
    <>
      {/* Face text — cube only */}
      <div>
        <div className="text-[11px] text-white/55 mb-2">Face Text</div>
        <div className="space-y-1.5">
          {FACE_LABELS.map((label, i) => (
            <TextInput
              key={i}
              label={label}
              labelWidth="w-12"
              value={(c.faces || DEFAULT_PRIMITIVES3D.faces)[i] || ''}
              onChange={(v) => updateFace(i, v)}
              maxLength={40}
              placeholder={`${label} text...`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function SphereParams({ c, update }) {
  return (
    <div>
      <div className="text-[10px] text-white/40 mb-1.5">Detail</div>
      <LevelPicker levels={SPHERE_DETAIL_LEVELS} value={c.sphereDetail ?? 2} onChange={(v) => update({ sphereDetail: v })} />
    </div>
  );
}

function TorusParams({ c, update }) {
  return (
    <div>
      <div className="text-[10px] text-white/40 mb-1.5">Tube Thickness</div>
      <LevelPicker levels={TORUS_TUBE_LEVELS} value={c.torusTube ?? 1} onChange={(v) => update({ torusTube: v })} />
    </div>
  );
}

function TorusKnotParams({ c, update }) {
  return (
    <div className="space-y-2">
      <SliderRow label="Winds P" min={1} max={7} step={1} value={c.knotP ?? 2} onChange={(v) => update({ knotP: v })} format={(v) => `${v}`} />
      <SliderRow label="Winds Q" min={1} max={9} step={1} value={c.knotQ ?? 3} onChange={(v) => update({ knotQ: v })} format={(v) => `${v}`} />
    </div>
  );
}

function IcosaParams({ c, update }) {
  return (
    <div>
      <div className="text-[10px] text-white/40 mb-1.5">Detail</div>
      <LevelPicker levels={ICOSA_DETAIL_LEVELS} value={c.icosaDetail ?? 0} onChange={(v) => update({ icosaDetail: v })} />
    </div>
  );
}

function WireframeParams({ c, update }) {
  return (
    <div>
      <div className="text-[10px] text-white/40 mb-1.5">Detail</div>
      <LevelPicker levels={SPHERE_DETAIL_LEVELS} value={c.wireDetail ?? 2} onChange={(v) => update({ wireDetail: v })} />
    </div>
  );
}

function MobiusParams({ c, update }) {
  const WIDTHS = [{ label: 'Narrow' }, { label: 'Medium' }, { label: 'Wide' }, { label: 'Extra' }];
  const TWISTS = [{ label: '1×' }, { label: '2×' }, { label: '3×' }];
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] text-white/40 mb-1.5">Strip Width</div>
        <LevelPicker levels={WIDTHS} value={c.mobiusWidth ?? 1} onChange={(v) => update({ mobiusWidth: v })} />
      </div>
      <div>
        <div className="text-[10px] text-white/40 mb-1.5">Half-Twists</div>
        <LevelPicker levels={TWISTS} value={(c.mobiusTwists ?? 1) - 1} onChange={(v) => update({ mobiusTwists: v + 1 })} cols={3} />
      </div>
    </div>
  );
}

function ParametricParams({ c, update }) {
  const AMP_LEVELS = [{ label: 'Flat' }, { label: 'Low' }, { label: 'Medium' }, { label: 'High' }, { label: 'Extreme' }];
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] text-white/40 mb-1.5">Surface Type</div>
        <LevelPicker
          levels={PARAMETRIC_TYPES}
          value={PARAMETRIC_TYPES.findIndex(t => t.id === (c.parametricType ?? 'wave'))}
          onChange={(i) => update({ parametricType: PARAMETRIC_TYPES[i].id })}
          labelKey="name"
        />
      </div>
      <div>
        <div className="text-[10px] text-white/40 mb-1.5">Amplitude</div>
        <LevelPicker levels={AMP_LEVELS} value={c.parametricAmplitude ?? 2} onChange={(v) => update({ parametricAmplitude: v })} cols={5} />
      </div>
    </div>
  );
}

/* ── Shape-specific parameter router ─────────────────────────────── */

function ShapeParams({ shape, c, update }) {
  switch (shape) {
    case 'cube':        return <CubeParams c={c} update={update} />;
    case 'sphere':      return <SphereParams c={c} update={update} />;
    case 'torus':       return <TorusParams c={c} update={update} />;
    case 'torusKnot':   return <TorusKnotParams c={c} update={update} />;
    case 'icosahedron': return <IcosaParams c={c} update={update} />;
    case 'wireframe':   return <WireframeParams c={c} update={update} />;
    case 'mobius':       return <MobiusParams c={c} update={update} />;
    case 'parametric':  return <ParametricParams c={c} update={update} />;
    default:            return null;
  }
}

/* ── Main editor component ───────────────────────────────────────── */

export function Primitives3DEditor({ primitives3d, onChange }) {
  const c = useMemo(() => ({ ...DEFAULT_PRIMITIVES3D, ...primitives3d }), [primitives3d]);
  const enabled = c.enabled;
  const shape = c.shape || 'cube';

  const update = (patch) => {
    onChange({ ...c, ...patch });
  };

  /* Custom render for shape picker items (icon + name) */
  const renderShapeItem = (item, isActive) => (
    <div className={cn('flex flex-col items-center gap-0.5 py-1.5 px-1', isActive ? '' : '')}>
      <span className="text-base leading-none">{item.icon}</span>
      <span className="text-[9px] text-white/60 leading-none">{item.name}</span>
    </div>
  );

  return (
    <div className="field-container">
      {/* Header + Enable toggle */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="module-header">3D Primitive Objects</div>
          <div className="module-desc">Three.js rendered primitive shapes.</div>
        </div>
        <ToggleSwitch
          checked={enabled}
          onChange={(v) => update({ enabled: v })}
        />
      </div>

      <CollapseTransition open={enabled}>
        <div className="space-y-4">

          {/* ── Shape picker ── */}
          <div>
            <div className="text-[11px] text-white/55 mb-2">Shape</div>
            <PresetGrid
              items={SHAPES}
              value={shape}
              onChange={(id) => update({ shape: id })}
              columns={4}
              renderItem={renderShapeItem}
            />
          </div>

          {/* ── Per-shape parameters ── */}
          <div className="pt-3 border-t border-white/8">
            <div className="text-[11px] text-white/55 mb-2">
              {SHAPES.find(s => s.id === shape)?.name || 'Shape'} Settings
            </div>
            <div className="space-y-3">
              <ShapeParams shape={shape} c={c} update={update} />
            </div>
          </div>

          {/* ── Scale ── */}
          <div className="pt-3 border-t border-white/8">
            <div className="text-[10px] text-white/40 mb-1.5">Scale</div>
            <LevelPicker levels={SHAPE_SCALE_LEVELS} value={c.shapeScale ?? 3} onChange={(v) => update({ shapeScale: v })} cols={5} />
          </div>

          {/* ── Rotation ── */}
          <div className="pt-3 border-t border-white/8">
            <div className="text-[11px] text-white/55 mb-2">Rotation</div>
            <div className="space-y-2">
              <SliderRow label="X" min={-180} max={180} step={1} value={c.rotateX} onChange={(v) => update({ rotateX: v })} format={(v) => `${v}°`} />
              <SliderRow label="Y" min={-180} max={180} step={1} value={c.rotateY} onChange={(v) => update({ rotateY: v })} format={(v) => `${v}°`} />
              <SliderRow label="Z" min={-180} max={180} step={1} value={c.rotateZ} onChange={(v) => update({ rotateZ: v })} format={(v) => `${v}°`} />
            </div>
          </div>

          {/* ── Position ── */}
          <div className="pt-3 border-t border-white/8">
            <div className="text-[11px] text-white/55 mb-2">Position</div>
            <div className="space-y-2">
              <SliderRow label="X" min={-4} max={4} step={0.1} value={c.posX ?? 0} onChange={(v) => update({ posX: v })} format={(v) => v.toFixed(1)} />
              <SliderRow label="Y" min={-4} max={4} step={0.1} value={c.posY ?? 0.5} onChange={(v) => update({ posY: v })} format={(v) => v.toFixed(1)} />
            </div>
          </div>

          {/* ── Camera ── */}
          <div className="pt-3 border-t border-white/8">
            <div className="text-[11px] text-white/55 mb-2">Camera</div>
            <div className="space-y-2">
              <div>
                <div className="text-[10px] text-white/40 mb-1.5">Focal Length</div>
                <LevelPicker levels={P3D_FOV_LEVELS} value={c.fov} onChange={(v) => update({ fov: v })} cols={5} />
              </div>
              <SliderRow label="Distance" min={2} max={12} step={0.1} value={c.camDist} onChange={(v) => update({ camDist: v })} format={(v) => v.toFixed(1)} />
              <SliderRow label="Pan X" min={-3} max={3} step={0.1} value={c.camX} onChange={(v) => update({ camX: v })} format={(v) => v.toFixed(1)} />
              <SliderRow label="Pan Y" min={-3} max={3} step={0.1} value={c.camY} onChange={(v) => update({ camY: v })} format={(v) => v.toFixed(1)} />
            </div>
          </div>

          {/* ── World Grid ── */}
          <div className="pt-3 border-t border-white/8">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-white/55">World Grid</div>
                <div className="text-[9px] text-white/35 mt-0.5">Reference grid on XZ plane</div>
              </div>
              <ToggleSwitch
                checked={!!c.showGrid}
                onChange={(v) => update({ showGrid: v })}
                size="sm"
              />
            </div>
          </div>

          {/* ── Colors ── */}
          <div className="pt-3 border-t border-white/8 space-y-3">
            <div className="text-[11px] text-white/55 mb-1">Colors</div>

            {/* Background */}
            <ColorSwatchGrid
              label="Scene Background"
              colors={GRADIENT_BASE_TONES}
              value={c.bgColor}
              onChange={(id) => update({ bgColor: id })}
              variant="grid"
              columns={6}
            />

            <ColorSwatchGrid label="Surface" variant="compact" colors={P3D_FACE_COLORS} value={c.faceColor} onChange={(id) => update({ faceColor: id })} />
            <ColorSwatchGrid label="Edges" variant="compact" colors={P3D_EDGE_COLORS} value={c.edgeColor} onChange={(id) => update({ edgeColor: id })} />
            {shape === 'cube' && (
              <ColorSwatchGrid label="Text" variant="compact" colors={P3D_TEXT_COLORS} value={c.textColor} onChange={(id) => update({ textColor: id })} />
            )}
          </div>

          {/* ── Opacity ── */}
          <div className="pt-3 border-t border-white/8 space-y-2">
            <SliderRow label="Surface α" min={0} max={50} step={1} value={c.faceOpacity} onChange={(v) => update({ faceOpacity: v })} format={(v) => `${v}%`} />
            <SliderRow label="Edge α" min={0} max={100} step={1} value={c.edgeOpacity} onChange={(v) => update({ edgeOpacity: v })} format={(v) => `${v}%`} />
          </div>

          {/* ── Darken ── */}
          <div className="pt-3 border-t border-white/8">
            <DarkenSlider
              label="Darken"
              value={c.darken ?? 0}
              onChange={(v) => update({ darken: v })}
            />
          </div>
        </div>
      </CollapseTransition>
    </div>
  );
}
